/**
 * The just-in-time PO page (BLU-1268, plan 90 section 5.4). A batch wrote this
 * order for blanks bought to order: staff go out, buy, fix the order to what
 * they bought and paid, snap the receipt, and press Complete. Complete records
 * COST ONLY: no stock moves, nothing is received.
 *
 * Built from the standard page's pieces (the PO action bar, the TopHeader
 * title, BackToTopPill) but none of its receive machinery: no receiving card,
 * no shipments, no invoices card. Mobile first: one column below `lg`, h-11
 * inputs and actions on a phone.
 *
 * States:
 *   To buy (submitted)  edit lines, prices, add blanks, pick a supplier when
 *                       there is none, receipt; then Bought as listed (nothing
 *                       edited) or Complete; Cancel ("you did not buy these").
 *                       Unsaved edits swap the actions for Discard + Save.
 *   Completed           read only; Undo (back to To buy) and Push to Xero
 *                       (once the org's just-in-time account is set).
 *   Cancelled           read only. Never deletable (the backend refuses).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCheck, ExternalLink, Loader2, Save, Undo2, Upload, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NoteLine } from '@/components/shared/NoteLine';
import { StatusPill } from '@/components/shared/StatusPill';
import { WarningNotice } from '@/components/shared/WarningNotice';
import RoleGuard from '@/components/auth/RoleGuard';
import { BackToTopPill } from '@/components/BackToTopPill';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { formatDay, useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import type { PurchaseOrder } from '@/features/purchase-orders';
import { useJustInTimeOrder } from '../hooks/useJustInTimeOrder';
import {
  EMPTY_JIT_DRAFT,
  type AddedJitLine,
  type JitDraft,
  type JitLineRow,
  buildJitRows,
  buildJitSavePayload,
  isBoughtAsListed,
  jitRowName,
  summarizeJitDraft,
} from '../lib/jit-lines';
import { PoActionBar } from './PoActionBar';
import { CompleteJustInTimeDialog } from './CompleteJustInTimeDialog';
import { JitLinesCard } from './JitLinesCard';
import { JitReceiptCard } from './JitReceiptCard';

/** Roles the batch screen admits (App.tsx `batches/:batchId`); anyone else sees plain text. */
const BATCH_ROLES: ReadonlySet<string> = new Set(['Super Admin', 'Admin', 'Warehouse Coordinator', 'Picker', 'Printer']);
/** Roles the Xero bill push admits (`coordinatorProcedure`). */
const XERO_ROLES: ReadonlySet<string> = new Set(['Super Admin', 'Admin', 'Warehouse Coordinator']);
const XERO_BILL_URL = 'https://go.xero.com/AccountsPayable/View.aspx?invoiceID=';
const SETTINGS_PO_TAB = '/settings?tab=purchase-orders';

const BAR_BUTTON = 'h-10 gap-1.5 px-3 text-xs sm:h-8';
const PRIMARY = cn(BAR_BUTTON, 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90');

interface JustInTimeOrderViewProps {
  order: PurchaseOrder;
  /** The PO list URL to go back to (filters kept). */
  ordersReturnTo: string;
}

export function JustInTimeOrderView({ order, ordersReturnTo }: JustInTimeOrderViewProps) {
  const { t } = useTranslation('orders');
  const dateLocale = useDateFnsLocale();
  const { organization } = useOrganization();
  const { role, canManagePurchaseOrders } = useRole();
  const jit = useJustInTimeOrder(order);

  const [draft, setDraft] = useState<JitDraft>(EMPTY_JIT_DRAFT);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);

  const isOpen = order.status === 'submitted' || order.status === 'draft';
  const canManage = canManagePurchaseOrders();
  const editable = isOpen && canManage;
  const currency = order.currency ?? organization?.default_currency ?? 'AUD';
  const supplierMissing = !order.supplier_id;
  const { suppliers, isLoading: suppliersLoading } = useSuppliers({ enabled: editable && supplierMissing });

  const rows = useMemo(
    () => buildJitRows(order.items ?? [], jit.outcomes.data, draft.added),
    [order.items, jit.outcomes.data, draft.added],
  );
  const summary = useMemo(() => summarizeJitDraft(rows, draft), [rows, draft]);
  const asListed = isBoughtAsListed(rows);
  const outcomes = jit.outcomes.data;

  const supplierName = draft.supplier?.name ?? order.supplier_name ?? null;
  usePageTitle(
    order.po_number,
    supplierName ?? t('jit.noSupplier'),
    useMemo(() => ({ label: t('jit.back'), to: ordersReturnTo }), [t, ordersReturnTo]),
  );

  // ── Draft edits ────────────────────────────────────────────────────────────
  const setQty = (skuId: string, text: string) => setDraft((d) => ({ ...d, qty: { ...d.qty, [skuId]: text } }));
  const setPrice = (skuId: string, text: string) => setDraft((d) => ({ ...d, price: { ...d.price, [skuId]: text } }));
  const removeRow = (row: JitLineRow) =>
    setDraft((d) =>
      row.added
        ? {
            ...d,
            added: d.added.filter((line) => line.skuId !== row.skuId),
            qty: omit(d.qty, row.skuId),
            price: omit(d.price, row.skuId),
          }
        : { ...d, qty: { ...d.qty, [row.skuId]: '0' } },
    );
  const addRow = (line: AddedJitLine) =>
    setDraft((d) => ({ ...d, added: [...d.added, line], qty: { ...d.qty, [line.skuId]: '1' } }));
  const pickSupplier = (supplierId: string) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (supplier) setDraft((d) => ({ ...d, supplier: { id: supplier.id, name: supplier.title } }));
  };

  const skuLabel = (skuId: string): string | null => {
    const row = rows.find((r) => r.skuId === skuId);
    return row ? jitRowName(row) : null;
  };

  const handleSave = async () => {
    const payload = buildJitSavePayload(order, rows, draft);
    if (!payload) return;
    const saved = await jit.save(payload, skuLabel);
    if (saved) setDraft(EMPTY_JIT_DRAFT);
  };

  // ── Bar ────────────────────────────────────────────────────────────────────
  const statusLabel =
    order.status === 'received'
      ? t('jit.status.completed')
      : order.status === 'cancelled'
        ? t('jit.status.cancelled')
        : t('jit.status.toBuy');

  const batchNumber = outcomes?.batchNumber ?? null;
  const batchId = outcomes?.batchId ?? order.batch_id ?? null;
  const batchCancelled = outcomes?.batchStatus === 'CANCELLED';
  const batchNode: ReactNode =
    batchId && batchNumber !== null ? (
      BATCH_ROLES.has(role ?? '') ? (
        <Link
          to={`/production/batches/${batchId}`}
          className="flex h-8 items-center rounded-md px-1 text-xs font-medium text-primary-strong underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('jit.batchLink', { number: batchNumber })}
        </Link>
      ) : (
        <span className="flex h-8 items-center text-xs text-muted-foreground">
          {t('jit.batchLink', { number: batchNumber })}
        </span>
      )
    ) : null;

  const accountCode = organization?.settings?.purchaseOrders?.justInTimeAccountCode?.trim() || null;
  const canPushXero =
    order.status === 'received' &&
    jit.xeroConnected &&
    XERO_ROLES.has(role ?? '') &&
    Boolean(accountCode) &&
    !supplierMissing;

  let actions: ReactNode = null;
  if (editable && summary.dirty) {
    const saveBlocked = summary.invalidCount > 0 || (summary.linesChanged && summary.totalUnits <= 0);
    actions = (
      <>
        <Button
          type="button"
          variant="outline"
          className={BAR_BUTTON}
          onClick={() => setDraft(EMPTY_JIT_DRAFT)}
          disabled={jit.pending.saving}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          {t('jit.discard')}
        </Button>
        <Button
          type="button"
          className={PRIMARY}
          onClick={() => void handleSave()}
          disabled={saveBlocked || jit.pending.saving}
          aria-busy={jit.pending.saving || undefined}
        >
          {jit.pending.saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <Save className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {jit.pending.saving ? t('jit.saving') : t('jit.save')}
        </Button>
      </>
    );
  } else if (editable) {
    const waiting = jit.outcomes.loading;
    const asListedBlocked = asListed && supplierMissing;
    actions = (
      <Button
        type="button"
        className={PRIMARY}
        onClick={() => setCompleteOpen(true)}
        disabled={waiting || asListedBlocked || jit.pending.completing}
        aria-describedby={asListedBlocked ? 'jit-needs-supplier' : undefined}
      >
        <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
        {asListed ? t('jit.boughtAsListed') : t('jit.complete')}
      </Button>
    );
  } else if (order.status === 'received' && canManage) {
    actions = (
      <>
        <Button
          type="button"
          variant="outline"
          className={cn(BAR_BUTTON, 'border-amber-200 text-amber-700 hover:bg-amber-50')}
          onClick={() => setUndoOpen(true)}
          disabled={jit.pending.undoing}
        >
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('jit.undo')}
        </Button>
        {canPushXero && (
          <Button
            type="button"
            className={PRIMARY}
            onClick={() => void jit.pushToXero()}
            disabled={jit.pending.pushing}
            aria-busy={jit.pending.pushing || undefined}
          >
            {jit.pending.pushing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {order.xero_invoice_id ? t('jit.xero.update') : t('jit.xero.push')}
          </Button>
        )}
      </>
    );
  }

  const destructive: ReactNode =
    editable && !summary.dirty ? (
      <Button
        type="button"
        variant="outline"
        className={cn(BAR_BUTTON, 'border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700')}
        onClick={() => setCancelOpen(true)}
        disabled={jit.pending.cancelling}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        {t('jit.cancel')}
      </Button>
    ) : null;

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="w-full flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <PoActionBar
            order={order}
            justInTime={{
              statusLabel,
              batch: batchNode,
              notice: batchCancelled ? (
                <StatusPill tone="warning" className="h-8 self-end">
                  {t('jit.batchCancelled')}
                </StatusPill>
              ) : null,
              actions,
              destructive,
            }}
          />

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 space-y-6">
              {batchCancelled && isOpen && (
                <WarningNotice role="status">{t('jit.batchCancelledBody')}</WarningNotice>
              )}

              {editable && supplierMissing && (
                <Card className="border-amber-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('jit.supplier.title')}</CardTitle>
                    <p id="jit-needs-supplier" className="text-sm text-muted-foreground">
                      {t('jit.supplier.help')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <label htmlFor="jit-supplier" className="sr-only">
                      {t('jit.supplier.label')}
                    </label>
                    <Select value={draft.supplier?.id ?? ''} onValueChange={pickSupplier} disabled={suppliersLoading}>
                      <SelectTrigger id="jit-supplier" className="h-11 w-full sm:h-9 sm:max-w-sm">
                        <SelectValue
                          placeholder={suppliersLoading ? t('jit.supplier.loading') : t('jit.supplier.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!suppliersLoading && suppliers.length === 0 && (
                      <NoteLine as="p" className="mt-2">
                        {t('jit.supplier.none')}{' '}
                        <Link to="/suppliers" className="font-medium text-primary-strong hover:underline">
                          {t('jit.supplier.manage')}
                        </Link>
                      </NoteLine>
                    )}
                  </CardContent>
                </Card>
              )}

              <JitLinesCard
                rows={rows}
                draft={draft}
                summary={summary}
                editable={editable}
                open={isOpen}
                supplierName={supplierName}
                currency={currency}
                locationId={order.location_id ?? null}
                outcomes={jit.outcomes}
                onQtyChange={setQty}
                onPriceChange={setPrice}
                onRemove={removeRow}
                onAdd={addRow}
              />
            </div>

            <div className="min-w-0 space-y-6">
              <JitReceiptCard
                files={jit.receipts.files}
                loading={jit.receipts.loading}
                failed={jit.receipts.failed}
                retrying={jit.receipts.retrying}
                onRetry={jit.receipts.retry}
                canManage={canManage && order.status !== 'cancelled'}
                uploading={jit.pending.uploading}
                deletingFileId={jit.pending.deletingFileId}
                onUpload={jit.uploadReceipt}
                onOpen={(fileId) => void jit.openReceipt(fileId)}
                onDelete={jit.deleteReceipt}
              />

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{t('jit.details.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">{t('jit.details.supplier')}</dt>
                    <dd className={cn('min-w-0 break-words', !supplierName && 'text-muted-foreground')}>
                      {order.supplier_id ? (
                        <Link to={`/suppliers/${order.supplier_id}`} className="text-primary-strong hover:underline">
                          {order.supplier_name}
                        </Link>
                      ) : (
                        supplierName ?? t('jit.noSupplier')
                      )}
                    </dd>
                    <dt className="text-muted-foreground">{t('jit.details.created')}</dt>
                    <dd>{formatDay(order.created_at, dateLocale)}</dd>
                    {order.status === 'received' && (
                      <>
                        <dt className="text-muted-foreground">{t('jit.details.completed')}</dt>
                        <dd>{formatDay(order.completed_date, dateLocale)}</dd>
                      </>
                    )}
                    {order.status === 'received' && jit.xeroConnected && (
                      <>
                        <dt className="text-muted-foreground">{t('jit.details.xero')}</dt>
                        <dd className="min-w-0">
                          <XeroState
                            order={order}
                            accountCode={accountCode}
                            supplierMissing={supplierMissing}
                          />
                        </dd>
                      </>
                    )}
                  </dl>
                  {isOpen && (
                    <NoteLine as="p" className="mt-3">
                      {t('jit.completeHint')}
                    </NoteLine>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <BackToTopPill />

        <CompleteJustInTimeDialog
          open={completeOpen}
          onOpenChange={setCompleteOpen}
          asListed={asListed}
          poNumber={order.po_number}
          pending={jit.pending.completing}
          onConfirm={() => jit.complete(asListed)}
        />

        <ConfirmDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          title={t('jit.cancelDialog.title', { po: order.po_number })}
          body={t('jit.cancelDialog.body')}
          confirmLabel={t('jit.cancelDialog.confirm')}
          tone="destructive"
          pending={jit.pending.cancelling}
          onConfirm={jit.cancel}
        />

        <ConfirmDialog
          open={undoOpen}
          onOpenChange={setUndoOpen}
          title={t('jit.undoDialog.title', { po: order.po_number })}
          body={t('jit.undoDialog.body')}
          confirmLabel={t('jit.undoDialog.confirm')}
          tone="primary"
          pending={jit.pending.undoing}
          onConfirm={jit.undoComplete}
        />
      </div>
    </RoleGuard>
  );
}

function omit(record: Record<string, string>, key: string): Record<string, string> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

/** Xero after Complete: the bill link once pushed, else why it cannot go yet. */
function XeroState({
  order,
  accountCode,
  supplierMissing,
}: {
  order: PurchaseOrder;
  accountCode: string | null;
  supplierMissing: boolean;
}) {
  const { t } = useTranslation('orders');
  if (order.xero_invoice_id) {
    return (
      <a
        href={`${XERO_BILL_URL}${order.xero_invoice_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
      >
        {t('jit.xero.sent')}
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    );
  }
  if (!accountCode) {
    return (
      <span className="text-muted-foreground">
        {t('jit.xero.accountUnset')}{' '}
        <Link to={SETTINGS_PO_TAB} className="font-medium text-primary-strong hover:underline">
          {t('jit.xero.openSettings')}
        </Link>
      </span>
    );
  }
  if (supplierMissing) return <span className="text-muted-foreground">{t('jit.xero.needsSupplier')}</span>;
  return <span className="text-muted-foreground">{t('jit.xero.notSent')}</span>;
}

/** A two-button confirm that stays open while its call runs and closes on success. */
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  tone,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: string;
  confirmLabel: string;
  tone: 'primary' | 'destructive';
  pending: boolean;
  onConfirm: () => Promise<boolean>;
}) {
  const { t } = useTranslation('orders');
  const handleConfirm = async () => {
    if (pending) return;
    if (await onConfirm()) onOpenChange(false);
  };
  return (
    <AlertDialog open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{t('jit.dialog.back')}</AlertDialogCancel>
          <Button
            onClick={() => void handleConfirm()}
            disabled={pending}
            aria-busy={pending || undefined}
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            className={cn('gap-1.5', tone === 'primary' && 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90')}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
